import { useQuery } from '@tanstack/react-query';
import type { CalendarEventResponse, CalendarEventType } from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { calendarDays, todayIso } from '../format';
import { useI18n } from '../i18n/I18nProvider';
import { Badge, Card, PageHeader, Row, Stack } from './primitives';
import { EmptyState, ErrorState, LoadingState } from './states';

const TONES: Record<CalendarEventType, 'warning' | 'info' | 'neutral'> = {
  Closure: 'warning', Holiday: 'warning', Event: 'info', Trip: 'info',
};

/**
 * What is coming up in the nursery's calendar, read only (FR-MSG-09, GAPS 4.8 and 5.3): closures, holidays, events and
 * trips from today to the API's default horizon. The administration enters them in the console.
 *
 * `classId` narrows the list to the whole nursery's entries and that class's, for a family; staff see every class's,
 * each marked with its class. The API serves the calendar to every member, so this is a filter for reading, not a
 * rule.
 */
export function CalendarList({ classId }: { classId?: number | null }) {
  const api = useApi();
  const { t, settings } = useI18n();

  const events = useQuery({
    queryKey: ['calendar', 'upcoming'],
    queryFn: () => api.calendar.list({ from: todayIso(settings) }),
  });

  const shown = (events.data ?? []).filter((event) =>
    classId === undefined || event.audienceClassId === null || event.audienceClassId === classId);

  return (
    <Stack>
      <PageHeader title={t('calendar.title')} lede={t('calendar.hint')} />
      {events.isPending && <LoadingState />}
      {events.isError && <ErrorState error={events.error} onRetry={() => void events.refetch()} />}
      {events.isSuccess && shown.length === 0 && <EmptyState icon="🗓️" title={t('calendar.empty')} />}
      {shown.map((event) => <CalendarEntry key={event.id} event={event} />)}
    </Stack>
  );
}

function CalendarEntry({ event }: { event: CalendarEventResponse }) {
  const { t, formatDate, formatTime, settings } = useI18n();
  const { from, to } = calendarDays(event, settings);
  const days = from === to ? formatDate(from) : t('calendar.range', { from: formatDate(from), to: formatDate(to) });
  const times = event.allDay ? null : t('calendar.range', { from: formatTime(event.startAt), to: formatTime(event.endAt) });

  return (
    <Card>
      <Stack tight>
        <Row between>
          <strong>{event.title}</strong>
          <Badge tone={TONES[event.eventType]}>{t(`calendar.type.${event.eventType}`)}</Badge>
        </Row>
        <span>{times ? `${days} · ${times}` : days}</span>
        {event.affectsAttendance && <span className="dg-card__hint">{t('calendar.closed')}</span>}
        {event.audienceClassName && (
          <span className="dg-card__hint">{t('calendar.forClass', { name: event.audienceClassName })}</span>
        )}
        {event.description && <p className="dg-card__hint">{event.description}</p>}
      </Stack>
    </Card>
  );
}
